/**
 * Qualtrics Site Intercept en SPAs (Gatsby).
 *
 * En XM, si el proyecto usa carga / evaluación manual (documentación Qualtrics):
 * - Una vez en la página: QSI.API.load() luego QSI.API.run().
 * - Varias veces en la misma página (p. ej. cada ruta SPA): unload → (isDebug en modo manual) → load → run.
 *
 * GATSBY_QUALTRICS_SPA_MODE:
 * - run-only   → solo run() (despliegue automático; SIE ya inicializó)
 * - load-run   → load() + run() (manual, primera evaluación sin unload previo)
 * - full       → unload() + load() + run() (re-evaluar en la misma “página”)
 * - manual     → igual que full + QSI.isDebug=true
 */

const POLL_MS = 120
const POLL_MAX = 60
const DEBOUNCE_MS = 120
const AFTER_LOAD_MS = 80

let debounceTimer

function getSpaMode() {
  const m = process.env.GATSBY_QUALTRICS_SPA_MODE
  if (
    m === 'run-only' ||
    m === 'load-run' ||
    m === 'full' ||
    m === 'manual'
  ) {
    return m
  }
  return 'run-only'
}

function formatQualtricsErr(e) {
  if (e === undefined) {
    return 'undefined (promesa rechazada sin valor; suele pasar si load() se llama cuando el proyecto ya está cargado)'
  }
  if (e === null) {
    return 'null'
  }
  if (e instanceof Error) {
    return e.message || e.name || String(e)
  }
  return String(e)
}

function spaLog(msg) {
  // eslint-disable-next-line no-console
  console.info(`[Qualtrics SPA] ${msg}`)
}

/**
 * @param {Record<string, string>} [properties] - Pares clave/valor que se aplican
 *   a window.QSI.API.properties antes de load()+run(), útiles para que Qualtrics
 *   seleccione la encuesta adecuada según el contexto de la página actual.
 * @returns {Promise<boolean>}
 */
async function tryQualtricsSpaReevaluate(properties) {
  if (typeof window === 'undefined') {
    return false
  }
  const api = window.QSI?.API
  if (!api || typeof api.run !== 'function') {
    return false
  }

  const mode = getSpaMode()
  const useUnload = mode === 'full' || mode === 'manual'

  if (useUnload && typeof api.unload === 'function') {
    try {
      await Promise.resolve(api.unload())
    } catch (e) {
      spaLog(`unload: ${formatQualtricsErr(e)}`)
    }
  }

  if (mode === 'manual' && window.QSI) {
    window.QSI.isDebug = true
  }

  // Según lo entregado esta sería una forma, no obstante leyendo la comunidad de Qualtrics esto pertenece al SDK de react native, es posible que se tenga que usar  window.qualtricsPage = properties en todo caso
  if (properties) {
    window.qualtricsPage = properties
    Object.entries(properties).forEach(([key, value]) => {
      api?.properties?.setString(key, value)
    })
  }

  const useLoad = mode !== 'run-only'

  if (useLoad && typeof api.load === 'function') {
    try {
      await Promise.resolve(api.load())
      await new Promise((r) => setTimeout(r, AFTER_LOAD_MS))
    } catch (e) {
      spaLog(
        `load() falló (${formatQualtricsErr(e)}). Si el proyecto es automático, prueba GATSBY_QUALTRICS_SPA_MODE=run-only.`,
      )
    }
  }

  try {
    await Promise.resolve(api.run())
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[Qualtrics SPA] run() rechazó:', formatQualtricsErr(e))
    return true
  }

  spaLog(`ejecutado (mode=${mode}).`)

  return true
}

/**
 * @param {Record<string, string>} [properties]
 */
export function scheduleQualtricsSpaReevaluate(properties) {
  if (typeof window === 'undefined') {
    return
  }

  if (debounceTimer) {
    window.clearTimeout(debounceTimer)
  }

  debounceTimer = window.setTimeout(() => {
    debounceTimer = undefined

    let n = 0
    const poll = async () => {
      const ok = await tryQualtricsSpaReevaluate(properties)
      if (ok) {
        return
      }
      n += 1
      if (n < POLL_MAX) {
        window.setTimeout(() => {
          void poll()
        }, POLL_MS)
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          '[Qualtrics] QSI.API no disponible (¿adblock, CSP o SIE bloqueado?).',
        )
      }
    }
    void poll()
  }, DEBOUNCE_MS)
}
